import { m } from 'framer-motion';
import { useEffect, useState } from 'react';
// @mui
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { varHover } from 'src/components/animate';
import Iconify from 'src/components/iconify';
import { useGetNotificationsQuery, useNotificationMarkAsReadMutation } from 'src/store/Reducer/settings';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { CircularProgress, Tooltip } from '@mui/material';
import { USER_TYPE_SHOPADMIN, USER_TYPE_SUPER_ADMIN } from 'src/config-global';
import { enqueueSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { setUnreadCount } from 'src/store/slices/userSlice';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box } from '@mui/system';
import NotificationItem from './notification-item';

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const dispatch = useDispatch();
  const smUp = useResponsive('up', 'sm');
  const { user } = useMockedUser();

  const role = user?.user_type === USER_TYPE_SUPER_ADMIN ? 'admin' : 'contractor';
  const [notificationMarkAsRead] = useNotificationMarkAsReadMutation();
  const [notifications, setNotifications] = useState([]);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);
  // Select only the field this component reacts to — selecting the whole
  // `state.user` slice re-rendered this on every setToken/setUser write too.
  const unreadCount = useSelector((state) => state.user.unreadCount) || 0;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const isShopAdmin=user?.user_type===USER_TYPE_SHOPADMIN;

  const { data ,refetch } = useGetNotificationsQuery(
    {
      url:
        user?.user_type === 'admin'
          ? '/admin/notifications'
          : '/contractor/notifications',
      pageno: page, 
      
    },
    {
      skip: !user||isShopAdmin,
      refetchOnMountOrArgChange: true,
    }
  );



  // ✅ Accumulate pages without duplicates
  useEffect(() => {
    if (data?.body) {
      setNotifications((prev) => {
        const merged = [...prev, ...data.body];
        const distinct = Array.from(new Map(merged.map((item) => [item._id, item])).values());
        // ✅ Sort by createdAt descending — newest always on top
        return distinct.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      });
  
      const total_pages = data?.metadata?.total_pages || 1;
      setTotalPages(total_pages);
      setHasMore(page < total_pages);
    }
  }, [data]);

  // ✅ Called by InfiniteScroll when user hits the bottom
  const fetchMoreData = () => {
    if (hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSingleMark = async (notificationId) => {
    if (!notificationId) return;

    const target = notifications.find((n) => n._id === notificationId);
    if (target?.is_read) {
      setSelectedNotificationId(null);
      return;
    }

    setNotifications((prev) =>
      prev.map((n) => (n._id === notificationId ? { ...n, is_read: true } : n))
    );

    const response = await notificationMarkAsRead({
      role,
      data: { notification_ids: [notificationId] },
    });

    if (!response?.error) {
      enqueueSnackbar(response?.data?.message, { variant: 'success' });
    }
    setSelectedNotificationId(null);
  };

  useEffect(() => {
    if (selectedNotificationId) {
      handleSingleMark(selectedNotificationId);
    }
  }, [selectedNotificationId]);

  const handleMarkAllAsRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    const response = await notificationMarkAsRead({ role, data: {} });
    if (!response?.error) {
      enqueueSnackbar(response?.data?.message, { variant: 'success' });
    }
  };

  const totalUnreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    setTotalUnread(totalUnreadCount);
  }, [totalUnreadCount]);
  useEffect(() => {
    if (unreadCount && !isShopAdmin) {
      setPage(1);
      refetch();
      setTotalUnread((prev) => prev + 1);
      dispatch(setUnreadCount(0));
    }
  }, [unreadCount]);


  useEffect(() => {
    if (drawer.value) {
      refetch();
    }
  }, [drawer.value]);

  // ──────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────

  const renderHead = (
    // ✅ flex-shrink: 0 → header never shrinks; stays fixed at top
    <Stack
      direction="row"
      alignItems="center"
      sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68, flexShrink: 0 }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnreadCount && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderList = (
    <Box
      id="scrollableDiv"
      sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}
    >
      <InfiniteScroll
        dataLength={notifications.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={25} />
          </Box>
        }
        endMessage={
          <Typography variant="body2" align="center" sx={{ py: 2, color: 'text.disabled' }}>
            You're all caught up 🎉
          </Typography>
        }
        // ✅ Points InfiniteScroll at our single scrollable container
        scrollableTarget="scrollableDiv"
        style={{ overflow: 'visible' }} // ✅ Prevent InfiniteScroll adding its OWN scrollbar
      >
        <List disablePadding>
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                setSelectedNotificationId={setSelectedNotificationId}
                onClose={drawer.onFalse}
              />
            ))
          ) : (
            <Typography variant="subtitle1" sx={{ p: 2 }} textAlign="center">
              No new notifications
            </Typography>
          )}
        </List>
      </InfiniteScroll>
    </Box>
  );

  return (
    <>
      {!isShopAdmin && <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnread} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>}

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{
          sx: {
            width: 1,
            maxWidth: 420,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {renderHead}
        <Divider sx={{ flexShrink: 0 }} />
        {renderList}
      </Drawer>
    </>
  );
}